<template>
  <b-modal
    id="manage-team"
    ref="manage-team"
    size="manage-team"
    centered
    no-close-on-backdrop
    no-close-on-esc
    hide-footer
    hide-header
    @hide="close"
  >
    <div class="modal-head">
      <h2 class="modal-title">
        {{ label.get('manage_team') }}
      </h2>
    </div>
    <div class="content-wrapper">
      <div class="table-heading">
        <span class="heading-label name">{{ label.get('name') }}</span>
        <span class="heading-label email">{{ label.get('email_address') }}</span>
        <span class="heading-label role">{{ label.get('role') }}</span>
        <span class="heading-label mobile-only">{{ label.get('team_member') }}</span>
      </div>
      <ul class="members">
        <li
          v-for="(item, idx) in memberList"
          v-show="!item.deleted"
          :key="'member-' + idx"
        >
          <div class="profile-pic">
            <i
              v-if="item.type === 'invite'"
              class="atto atto-avatar"
            />
            <span
              v-if="!item.avatar && item.type==='member'"
              class="initials"
            >
              {{ item.initials }}
            </span>
            <img
              v-if="item.avatar"
              :src="item.avatar"
              alt=""
              width="40"
              height="40"
            >
          </div>
          <div class="member-details">
            <h4 class="name">
              {{ item.name }}
            </h4>
            <p
              v-if="item.type==='member'"
              class="email"
            >
              {{ item.email }}
            </p>
            <p
              v-else
              class="email"
            >
              {{ item.label }}
            </p>
          </div>
          <div class="role">
            <p
              v-if="item.id === ownerId"
              class="owner"
            >
              Owner
            </p>
            <select-dropdown-component
              v-if="item.id !== ownerId"
              :value="item.role_id"
              :index="idx"
              :options="roles"
              @onOptionSelected="onRoleChanged"
            />
          </div>
          <button
            v-if="!item.deleting && item.id !== ownerId"
            class="delete-member"
            @click="deleteMember(item)"
          >
            <i class="atto atto-delete" />
          </button>
          <transition
            v-if="item.confirm_delete && !item.deleting"
            name="fade"
          >
            <div class="confirm-delete">
              <span class="confirm-msg">{{label.get('are_you_sure')}}</span>
              <button
                class="confirm-delete-btn"
                @click="confirmDeleteMember(idx, item)"
              >
                {{ label.get('delete') }}
              </button>
              <button
                class="cancel-delete"
                @click="cancelConfirmDelete(item)"
              >
                <i class="atto atto-close-btn" />
              </button>
            </div>
          </transition>
        </li>
        <li
          v-for="(invite, idx) in invites"
          class="pending-invitation"
          :key="'invite' + idx"
        >
          <div class="no-profile-pic" />
          <div class="pending-input">
            <input
              v-model="invite.email"
              type="email"
              :placeholder="label.get('enter_email_address')"
              :class="{ 'has-error': invite.error }"
            >
          </div>
          <div class="role">
            <select-dropdown-component
              :index="idx"
              :options="roles"
              :value="invite.role"
            />
          </div>
          <button
            class="delete-member"
            @click="deleteNewInvite(idx)"
          >
            <i class="atto atto-delete" />
          </button>
        </li>
      </ul>
      <div
        class="add-new"
        @click="addNewMember"
      >
        <i class="atto atto-add-more" />
        <h4>{{ label.get('add_new_member') }}</h4>
      </div>
    </div>
    <div class="modal-footer">
      <p
        v-if="invalidEmails"
        class="alert-message"
        v-html="errorMessage"
      />
      <button
        type="reset"
        class="atto-btn cancel"
        @click="close"
      >
        {{ label.get('cancel') }}
      </button>
      <button
        type="submit"
        class="atto-btn submit"
        @click="saveChanges"
      >
        {{ label.get('save_changes') }}
      </button>
    </div>
  </b-modal>
</template>

<script>
    import SelectDropdownComponent from './SelectDropdownComponent';
    import MemberService from '../../features/settings/services/MemberService';
    import UtilsService from '../services/UtilsService';
    import SubscriptionService from "../../features/subscription/services/SubscriptionService";

    export default {
        name: "ManageTeamComponent",
        components: {
            SelectDropdownComponent
        },
        props: {
            members: {
              type: Array,
              default: () => {
                return [];
              }
            },
        },
        data() {
            return {
                ownerId: this.$store.state.company.user_id,
                memberList: UtilsService.copyObject(this.members, true),
                invites: [],
                invalidEmails: false,
                errorMessage: '',
                roles: [
                    {
                        value: 1,
                        label: this.label.get('admin')
                    },
                    {
                        value: 2,
                        label: this.label.get('manager')
                    },
                    {
                        value: 3,
                        label: this.label.get('employee')
                    }
                ]
            }
        },
        mounted() {
            document.body.appendChild(this.$el);
            this.$refs['manage-team'].show();
        },
        methods: {
            onRoleChanged(option, idx) {
                this.memberList[idx].role_id = option.value;
                const member = this.members.find((item) => {
                    return item.id === this.memberList[idx].id && item.type === this.memberList[idx].type;
                });

                this.memberList[idx].changed = !(member !== null && member.role_id === option.value);
            },

            addNewMember() {
                let emptyFields = false;
                this.invites.forEach((item) => {
                    if (item.email.trim() === '') {
                        emptyFields = true;
                    }
                });
                if (!emptyFields) {
                    this.invites.push({
                        id: null,
                        email: '',
                        role: 3,
                        error: false
                    });
                }
            },

            deleteNewInvite(idx) {
                this.invites.splice(idx, 1);
                this.invalidEmails = false;
            },

            deleteMember(item) {
                item.confirm_delete = true;
            },

            confirmDeleteMember(idx, item) {
                if (item.id === null) {
                    this.members.splice(idx, 1);
                    return;
                }
                this.memberList[idx].deleted = true;
            },

            saveChanges() {
                this.resetErrors();
                if (!this.validateEmails()) {
                    this.errorMessage = this.label.get('please_add_a_valid_email');
                    this.invalidEmails = true;
                    return;
                }

                const invites = this.invites.filter((item) => {
                    return item.email !== ''
                });
                const data = invites.map((item, idx) => {
                    item.index = idx;
                    return item;
                });

                if (data.length > 0) {
                    this.$store.commit('showLoading');
                    SubscriptionService.pendingInvites({
                        invites: data
                    }).then((result) => {
                        this.$store.commit('hideLoading');
                        this.$root.$emit('onManageTeamClose', true);
                    }).catch((error) => {
                        this.$store.commit('hideLoading');
                        if (error.response.status === 422) {
                            const errors = error.response.data;
                            errors.forEach((err) => {
                                this.invites[err.index].error = true;
                                this.errorMessage += err.message + '<br>';
                            });
                            this.invalidEmails = true;
                        }
                    });
                }

                const membersChanged = this.memberList.filter((item) => {
                    return item.deleted || item.changed;
                });

                if (membersChanged.length > 0) {
                    this.$store.commit('showLoading');
                    MemberService.updateMembesAndInvites({members: membersChanged}).then((result) => {
                        this.memberList.forEach((item) => {
                            if (item.deleted) {
                                const member = this.members.find((member) => {
                                    return member.id === item.id
                                });
                                if (member !== null) {
                                    const idx = this.members.indexOf(member);
                                    this.members.splice(idx, 1);
                                }
                            }
                        });
                        this.$store.commit('hideLoading');
                        this.$root.$emit('onManageTeamClose');
                    }).catch((error) => {
                    });
                } else {
                    this.$root.$emit('onManageTeamClose');
                }
            },

            cancelConfirmDelete(item) {
                item.confirm_delete = false;
            },

            close() {
                this.$root.$emit('onManageTeamClose');
            },
            resetErrors() {
                this.invites.forEach((item) => {
                    item.error = false;
                });
            },
            validateEmails() {
                let validEmails = true;
                const regex = /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

                this.invites.forEach((item) => {
                    if (item.email.trim() !== '' && !regex.test(item.email.trim())) {
                        item.error = true;
                        validEmails = false;
                    }
                });

                return validEmails;
            }
        }
    }
</script>

<style lang="scss">

    .modal-manage-team {
        max-width: 756px!important;

        .modal-head {
            padding: 49px 81px 29px;
            display: flex;

            .modal-title {
                font-size: 1.5rem;
                line-height: 2rem;
                font-family: "Roboto", sans-serif;
                font-weight: 700;
                color: #262626;
            }
        }

        .modal-body {
            .content-wrapper {
                padding: 0!important;

                .table-heading {
                    background-color: #fafafa;
                    border-top: 1px solid #efefef;
                    border-bottom: 1px solid #efefef;
                    padding: 4px 0 4px 81px;
                    display: flex;
                    flex-direction: row;

                    span {
                        font-family: "Roboto", sans-serif;
                        font-size: 0.75rem;
                        line-height: 1.5rem;
                        color: #999;
                        margin-right: 8px;

                        &.role {
                            flex-grow: 1;
                        }

                        &.email,
                        &.name {
                            width: 224px;
                        }

                        &.mobile-only {
                            display: none;
                        }
                    }
                }

                .members {
                    > li {
                        display: flex;
                        flex-direction: row;
                        padding: 16px 24px 16px 20px;
                        border-bottom: 1px solid #efefef;
                        align-items: center;
                        position: relative;

                        .profile-pic {
                            width: 40px;
                            min-width: 40px;
                            max-width: 40px;
                            height: 40px;
                            border-radius: 100%;
                            margin-right: 20px;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            align-content: center;

                            img {
                                border-radius: 100%;
                            }

                            i.atto-avatar {
                                font-size: 40px;
                            }

                            span.initials {
                                display: inline-flex;
                                align-items: center;
                                justify-content: center;
                                font-size: 0.875rem;
                                line-height: 1.5rem;
                                font-weight: 400;
                                font-family: "Roboto", sans-serif;
                                color: #999;
                                background-color: #efefef;
                                border-radius: 100%;
                                width: 100%;
                                height: 100%;
                                text-transform: uppercase;
                            }
                        }

                        .member-details {
                            display: flex;
                            flex-direction: row;

                            h4 {
                                font-family: "Roboto", sans-serif;
                                font-weight: 500;
                                font-size: 1rem;
                                line-height: 1.5rem;
                                display: inline-flex;
                                width: 220px;
                                margin-right: 12px;
                            }

                            p.email {
                                width: 216px;
                                margin-bottom: 0;
                                margin-right: 16px;
                                font-family: "Roboto", sans-serif;
                                font-weight: 400;
                                color: #262626;
                                font-size: 0.875rem;
                                line-height: 1.5rem;
                            }
                        }

                        .role {
                            width: 140px;
                            margin-right: 0px;

                            .atto-select > .select-heading > span {
                                min-width: 96px;
                            }

                            .atto-select > .select-heading > i {
                                margin-right: 0;
                            }

                            p.owner {
                                width: 140px;
                                margin-bottom: 0;
                                margin-right: 16px;
                                margin-left: 10px;
                                font-family: "Roboto", sans-serif;
                                font-weight: 400;
                                color: #262626;
                                font-size: 0.875rem;
                                line-height: 1.5rem;
                            }
                        }


                        button.delete-member {
                            margin: 0;
                            padding: 0;
                            width: 24px;
                            height: 24px;
                            cursor: pointer;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            margin-left: auto;

                            i {
                                font-size: 18px;
                                color: #c7c7c7;
                                @include base-transition(ease all 0.15s);
                            }

                            &:hover {
                                i {
                                    color: #262626;
                                }
                            }
                        }

                        &.pending-invitation {
                            .no-profile-pic {
                                width: 40px;
                                height: 40px;
                                margin-right: 20px;
                            }

                            .pending-input {
                                width: 320px;

                                input {
                                    color: #262626;
                                    width: 100%;
                                    font-family: "Roboto", sans-serif;
                                    font-size: 0.875rem;
                                    line-height: 1.5rem;
                                    font-weight: 400;
                                    padding: 8px 12px;
                                    border: 2px solid #efefef;
                                    @include base-transition(ease all 0.15s);

                                    &:focus {
                                        border: 2px solid $atto-not-active;
                                    }

                                    &.has-error {
                                        border-color: #E05656;
                                        box-shadow: none;
                                    }
                                }

                            }

                            .pending-label {
                                width: 216px;
                                margin-right: 16px;
                                color: #999;
                                font-family: "Roboto", sans-serif;
                                font-size: 0.875rem;
                                line-height: 1.5rem;
                                font-weight: 400;
                            }

                            .role {
                                margin-right: 24px;
                                margin-left: auto;
                            }

                            .delete-member {
                                margin-left: inherit;
                            }
                        }

                    }
                }

                .add-new {
                    display: flex;
                    flex-direction: row;
                    align-items: center;
                    padding: 16px 20px;
                    border-bottom: 1px solid #efefef;

                    i {
                        width: 40px;
                        height: 40px;
                        display: flex;
                        justify-content: center;
                        align-items: center;
                        font-size: 16px;
                        color: $atto-teal;
                        margin-right: 20px;
                    }

                    h4 {
                        width: 224px;
                        color: #999999;
                        font-family: "Roboto", sans-serif;
                        font-size: 1rem;
                        font-weight: 500;
                        line-height: 1.5rem;
                        @include base-transition(ease all 0.15s);
                    }

                    &:hover {
                        cursor: pointer;

                        h4 {
                            color: #262626;
                        }
                    }
                }

                .confirm-delete {
                    position: absolute;
                    right: 0;
                    top: 0;
                    background-color: #fff;
                    width: 212px;
                    height: 100%;
                    display: flex;
                    align-items: center;
                    flex-direction: row;
                    justify-content: flex-end;
                    padding-left: 0px;
                    padding-right: 24px;

                    .confirm-msg {

                        font-size: 0.875rem;
                        line-height: 1.75rem;
                        font-family: "Roboto", sans-serif;
                        font-weight: 400;
                        color: #262626;

                    }

                    .confirm-delete-btn {

                        font-size: 0.875rem;
                        line-height: 1.75rem;
                        font-family: "Roboto", sans-serif;
                        font-weight: 500;
                        color: #262626;
                        margin-left: 12px;
                        padding: 0;
                        cursor: pointer;

                    }

                    .cancel-delete {
                        width: 24px;
                        height: 24px;
                        display: flex;
                        justify-content: center;
                        align-items: center;
                        margin-left: 24px;
                        padding: 0;
                        cursor: pointer;

                        i {
                            font-size: 14px;
                            color: #c7c7c7;
                            @include base-transition(ease color 0.15s);
                        }

                        &:hover {
                            i {
                                color: $atto-not-active;
                            }
                        }
                    }
                }
            }
        }


        .modal-footer {
            padding: 32px 49px 49px;
            display: flex;
            justify-content: flex-end;
            align-items: center;

            .alert-message {
                font-size: 0.75rem;
                line-height: 1.25rem;
                font-family: "Roboto", sans-serif;
                color: #E05656;
                font-weight: 500;
                align-self: flex-start;
                display: inline-flex;
                margin-right: auto;
                margin-left: 31px;
                margin-bottom: 0;
            }
        }
    }

    @media (max-width: 768px) {
        .modal-manage-team {

            margin: 0;
            min-height: 100vh;


            .modal-content {
                min-height: 100vh;
            }

            .modal-head,
            .modal-footer {
                padding: 20px 20px;
            }

            .modal-body {
                .content-wrapper {
                    .table-heading {
                        padding-left: 20px;

                        span {
                            display: none;
                        }

                        span.mobile-only {
                            display: block;
                        }
                    }

                    .members {
                        li {
                            padding: 16px 20px;

                            .profile-pic,
                            .role {
                                display: none;
                            }

                            .member-details {
                                flex-direction: column;
                            }
                        }

                        .pending-invitation {
                            padding: 20px;

                            .no-profile-pic {
                                display: none;
                            }

                            .pending-input {
                                max-width: 55%;
                                margin-right: 12px;

                                input {
                                    padding: 6px 7px !important;
                                }
                            }

                            .role {
                                max-width: calc(50% - 12px);
                                margin-right: 8px !important;
                                display: block;

                                .atto-select {
                                    max-width: 120px;

                                    > .select-heading {
                                        span {
                                            min-width: inherit;
                                            max-width: calc(100% - 24px);
                                        }
                                    }
                                }
                            }
                        }
                    }

                    .add-new {
                        min-height: 81px;
                        padding: 15px 20px;

                        i {
                            width: 24px;
                            height: 24px;
                            margin-right: 7px;
                        }
                    }


                    .confirm-delete {
                        width: 100%;
                        padding-right: 20px;
                    }


                }
            }

            .modal-footer {
                flex-direction: column;

                .atto-btn {
                    width: 100% !important;

                    &.cancel {
                        margin-right: 0 !important;
                        order: 2;
                        margin-top: 10px;
                        margin-bottom: 0;
                    }
                }
            }
        }
    }


    @media only screen and (max-width: 768px) and (orientation: landscape) {
        .modal-manage-team {
            .modal-footer {
                flex-direction: row;

                .atto-btn {
                    &.cancel {
                        margin-top: 0;
                        order: 0;
                        margin-right: 10px;
                    }
                }
            }
        }
    }
</style>
